import { Span } from '@opentelemetry/api';

function isPerformanceResourceTiming(
  entry: PerformanceEntry,
): entry is PerformanceResourceTiming {
  return entry.entryType === 'resource';
}

function originFromLocation() {
  return window.location.origin;
}

function performanceFromWindow() {
  return window.performance;
}

export type OriginSource = () => string;
export type PerformanceSource = () => Performance;

export interface ResourceAttributesOptions {
  originSource?: OriginSource;
  performanceSource?: PerformanceSource;
}

export class ResourceAttributes {
  private readonly originSource: OriginSource;
  private readonly performanceSource: PerformanceSource;

  constructor(options: ResourceAttributesOptions = {}) {
    this.originSource = options.originSource ?? originFromLocation;
    this.performanceSource = options.performanceSource ?? performanceFromWindow;
  }

  protected isFetchResource(resource: PerformanceResourceTiming) {
    // see: https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming/initiatorType
    return (
      resource.initiatorType === 'fetch' || resource.initiatorType === 'xmlhttprequest'
    );
  }

  protected isOriginResource(resource: PerformanceResourceTiming) {
    return resource.name.startsWith(this.originSource());
  }

  protected isResourceFresh(resource: PerformanceResourceTiming) {
    // the resource timing 2 spec claims that transferSize should be 0 when the resource was cached
    // locally, but this does not seem to hold true
    // see: https://www.w3.org/TR/resource-timing-2/#dom-performanceresourcetiming-transfersize
    return resource.transferSize === 0 || resource.duration === 0;
  }

  protected isResourceValidated(resource: PerformanceResourceTiming) {
    // the transferSize is 300 when the resource was pulled from cache, but that alone does not
    // appear to signify validated (despite what the spec says)
    // see: https://www.w3.org/TR/resource-timing-2/#dom-performanceresourcetiming-transfersize
    return resource.transferSize === 300 && resource.duration > 0;
  }

  protected isResourceMeasurable(resource: PerformanceResourceTiming) {
    // only resources from the same origin are considered, as CORS will interfere with accurate timing info
    return this.isOriginResource(resource) && !this.isFetchResource(resource);
  }

  addAttributes(span: Span): Span {
    const resources = this.performanceSource()
      .getEntriesByType('resource')
      .filter(isPerformanceResourceTiming)
      .filter((r) => this.isResourceMeasurable(r));
    const fresh = resources.filter((r) => this.isResourceFresh(r));
    const validated = resources.filter((r) => this.isResourceValidated(r));
    span.setAttribute('trp.resources.numFresh', fresh.length);
    span.setAttribute(
      'trp.resources.numRetrieved',
      resources.length - fresh.length - validated.length,
    );
    span.setAttribute('trp.resources.numValidated', validated.length);
    span.setAttribute('trp.resources.total', resources.length);
    return span;
  }
}
